import React, { useEffect, useMemo, useState } from "react";
import { useDropzone, type Accept, type FileRejection } from "react-dropzone";
import toast from "react-hot-toast";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";

import Table, { TableColumn } from "../table/Table";
import TableAddFileRow from "../table/TableAddFileRow";
import HelpHint from "../help-hint/HelpHint";
import MediaLabel, { type MediaLabelTone } from "../media-label/MediaLabel";
import ResponseChip from "../response-chip/ResponseChip";
import MediaPreview from "../media-preview/MediaPreview";
import DateDisplay, { DATE_DISPLAY_TYPE } from "../date-display/DateDisplay";
import DropdownMenu, { DropdownMenuItem } from "../dropdown-menu/DropdownMenu";
import TakeLabel from "../label/TakeLabel";
import ToolTip from "../tooltip/ToolTip";
import { getFileIconFromExtension } from "../file-icon/fileIcon";
import {
  useAudioAudition,
  type AuditionItem,
  type AuditionRowProps,
} from "../audio-preview/core/useAudioAudition";
import AuditionPlayButton from "../audio-preview/AuditionPlayButton";
import TranscriptionStatusCell from "../transcription/TranscriptionStatusCell";
import { isTranscribableFileType } from "../transcription/transcriptionTargets";
import {
  hasTranscriptionPreview,
  type TranscriptionCellInfo,
} from "../../hooks/useTranscriptionStatuses";
import { formatDropRejection, withExtensionFallback } from "../../utils/fileType";

/**
 * ショットのメディアアイテムを表形式で表示・編集するテーブル。
 * 作品登録（MySiteWorks）と同じグローバル `Table` + dnd-kit 行 D&D +
 * インライン編集（名前・ファイル名）のパターンに揃えている。
 * 行の操作（ダウンロード / 文字起こし / 削除）は行末の3点リーダーに集約する
 * （トークルームのファイル一覧と同じ構成。メニュー項目は
 * `onDownload` / `onDelete` などコールバックの有無で構築されるため、
 * 共有・差し替え等は項目追加だけで拡張できる）。
 *
 * 行の構造:
 * - variation 行: 独立したバリエーション。名前のインライン編集が可能
 * - history 行: 同じ名前で追加された過去テイク。バリエーションの直下に
 *   インデント + テイク番号バッジ付きでぶら下がる。名前も編集可能
 *   （別の名前にリネームすると独立したバリエーションや別グループへの
 *   移動になる）
 *
 * 比較グループ = オプション（候補）（Preview の ComparisonSlide と同じ命名ルール）:
 * - 「ベース名_a」「ベース名_b」のように末尾が `_` + アルファベット1文字で、
 *   同じベース名のバリエーションが2件以上あるとき、それらはクライアントに
 *   選んでもらう「オプション（候補）」として Preview 上でまとめて比較表示
 *   される。本テーブルでも隣接配置 + 青い左アクセント + 連結アイコンで
 *   グループであることを視覚化し、並び替え（D&D・ヘッダーソート）でも
 *   グループを崩さない。
 *
 * 並び替え:
 * - 行頭ハンドルの D&D（グループ単位）
 * - 名前 / ファイル名 / 更新日時 のヘッダークリックソート（グループ単位）
 * いずれも確定した並びを `onReorder(orderedVariationIds)` で親へ通知する。
 */

export interface MediaTableRow {
  id: string;
  /** variation: バリエーション / history: 過去テイク */
  kind: "variation" | "history";
  /** バリエーション名（インライン編集可） */
  name: string;
  fileName: string;
  /** ファイル種別（audio / video など）。文字起こしの対象判定に使う */
  fileType?: string | null;
  /** サムネイル URL。無ければ mediaSrc、それも無ければファイルアイコン表示 */
  thumbnail?: string | null;
  mediaSrc?: string;
  updatedAt?: string;
  /** 履歴行のテイク番号（例: テイク2） */
  takeNumber?: number;
  /** 送信側タグの表示ラベル（例: オススメ）。undefined ならタグ列自体を非表示 */
  producerTag?: string | null;
  /** 送信側タグのカラートーン（`PRODUCER_TAG_TONES` 参照）。未指定は outline */
  producerTagTone?: MediaLabelTone;
  /** 受信側タグの表示ラベル（例: OK）。undefined ならタグ列自体を非表示 */
  clientTag?: string | null;
  /** ファイルサイズの表示文字列（例: 12.3 MB）。undefined ならサイズ列自体を非表示 */
  sizeLabel?: string;
  /** 名前の横に表示するステータスラベル（例: 「MA」= MA シーケンスにリンク済み） */
  statusLabel?: string | null;
  /** ステータスラベルのカラートーン。既定 blue */
  statusLabelTone?: MediaLabelTone;
}

export interface MediaItemTableProps {
  rows: MediaTableRow[];
  /** 名前確定時（trim 済み・変更ありのときのみ呼ばれる） */
  // eslint-disable-next-line no-unused-vars
  onRename?: (id: string, newName: string) => void;
  /** ファイル名確定時（trim 済み・変更ありのときのみ呼ばれる）。指定時はファイル名がインライン編集可 */
  // eslint-disable-next-line no-unused-vars
  onRenameFile?: (id: string, newFileName: string) => void;
  /** D&D / ヘッダーソートでの並び替え確定時。バリエーション id の新しい並び順を渡す */
  // eslint-disable-next-line no-unused-vars
  onReorder?: (orderedVariationIds: string[]) => void;
  /** 行のメディアをダウンロードする（3点リーダー内） */
  // eslint-disable-next-line no-unused-vars
  onDownload?: (id: string) => void;
  /** 行削除（3点リーダー内） */
  // eslint-disable-next-line no-unused-vars
  onDelete?: (id: string) => void;
  /**
   * 行の文字起こしを開く（3点リーダー内）。
   * 音声・動画の行にのみ項目が出る（`row.fileType` で判定）。
   */
  // eslint-disable-next-line no-unused-vars
  onTranscribe?: (id: string) => void;
  /** 行 id -> 文字起こしステータス。名前の横にチップ / 本文プレビューを出す */
  transcriptionStatuses?: Record<string, TranscriptionCellInfo>;
  emptyMessage?: string;
  /** 指定すると表の右上にヘルプアイコン（ツールチップ）を表示する */
  helpText?: string;
  /** テーブル全体の最小幅(px)。狭い画面では横スクロール */
  minWidth?: number;
  /**
   * ドロップ / ファイル選択で追加されたファイルを受け取る。
   * 指定するとテーブル全体がドロップゾーンになり、末尾に「+」行
   * （クリックでファイル選択ダイアログ）を表示する。
   */
  // eslint-disable-next-line no-unused-vars
  onFilesSelected?: (files: File[]) => void;
  /** 受け付けるファイル種別（react-dropzone の Accept 形式） */
  accept?: Accept;
  /** アップロード処理中など、ファイル追加を一時的に無効化する */
  addFilesDisabled?: boolean;
  /** アップロード進捗など、末尾「+」行の直前に差し込む全幅コンテンツ */
  uploadingRow?: React.ReactNode;
}

/** Preview の ComparisonSlide と同じ「ベース名_a」検出ルール */
const COMPARISON_SUFFIX_RE = /^(.+)_([a-zA-Z])$/;

/** 試聴（オーディション）対象になるオーディオ拡張子 */
const AUDIO_EXT_RE = /\.(mp3|wav|aac|m4a|ogg|flac)$/i;

/** 再生可能なオーディオ行か（ファイル名 or メディア URL の拡張子で判定） */
const isAudioRow = (row: MediaTableRow): boolean =>
  !!row.mediaSrc &&
  (AUDIO_EXT_RE.test(row.fileName) ||
    AUDIO_EXT_RE.test(row.mediaSrc.split("?")[0]));

/** インライン編集（名前 / ファイル名）で使う入力欄のスタイル */
const INLINE_INPUT_STYLE: React.CSSProperties = {
  display: "block",
  flex: 1,
  minWidth: 0,
  width: "100%",
  padding: "1px 6px",
  fontSize: 13,
  lineHeight: "150%",
  height: 24,
  border: "none",
  outline: "none",
  boxShadow: "none",
  background: "transparent",
};

/** 比較グループの行ブロックを視覚的に束ねる左アクセント */
const COMPARISON_ACCENT_STYLE: React.CSSProperties = {
  borderLeft: "3px solid var(--soremo-blue)",
};

// ===== 表示単位の構築 =====

/** 1 バリエーション（variation 行 + その履歴行） */
interface VariationBlock {
  variation: MediaTableRow;
  history: MediaTableRow[];
}

/** 並び替えの最小単位（単独バリエーション or 比較グループ） */
interface DisplayUnit {
  /** 先頭バリエーションの id（D&D の sortable id に使う） */
  id: string;
  /** 比較グループならベース名、単独なら null */
  comparisonBase: string | null;
  blocks: VariationBlock[];
}

const comparisonLetter = (name: string): string =>
  name.match(COMPARISON_SUFFIX_RE)?.[2].toUpperCase() ?? "";

const buildDisplayUnits = (rows: MediaTableRow[]): DisplayUnit[] => {
  // variation 行を起点に、後続の history 行をぶら下げてブロック化する
  const blocks: VariationBlock[] = [];
  for (const row of rows) {
    if (row.kind === "variation") {
      blocks.push({ variation: row, history: [] });
    } else if (blocks.length > 0) {
      blocks[blocks.length - 1].history.push(row);
    }
  }

  // 同じベース名のバリエーションが2件以上あるベースのみ比較グループ化（Preview と同条件）
  const baseCount = new Map<string, number>();
  blocks.forEach((block) => {
    const m = block.variation.name.match(COMPARISON_SUFFIX_RE);
    if (m) baseCount.set(m[1], (baseCount.get(m[1]) || 0) + 1);
  });

  const units: DisplayUnit[] = [];
  const unitByBase = new Map<string, DisplayUnit>();
  blocks.forEach((block) => {
    const m = block.variation.name.match(COMPARISON_SUFFIX_RE);
    const base = m && (baseCount.get(m[1]) ?? 0) >= 2 ? m[1] : null;
    if (base) {
      let unit = unitByBase.get(base);
      if (!unit) {
        unit = { id: block.variation.id, comparisonBase: base, blocks: [] };
        unitByBase.set(base, unit);
        units.push(unit);
      }
      unit.blocks.push(block);
    } else {
      units.push({
        id: block.variation.id,
        comparisonBase: null,
        blocks: [block],
      });
    }
  });

  // 比較グループ内はサフィックス文字順（A, B, C ...）に揃える
  units.forEach((unit) => {
    if (unit.comparisonBase) {
      unit.blocks.sort((a, b) =>
        comparisonLetter(a.variation.name).localeCompare(
          comparisonLetter(b.variation.name),
        ),
      );
    }
  });

  return units;
};

const flattenVariationIds = (units: DisplayUnit[]): string[] =>
  units.flatMap((unit) => unit.blocks.map((block) => block.variation.id));

// ===== ソート =====

type SortField = "name" | "file" | "date";
type SortOrder = "asc" | "desc";

interface SortState {
  field: SortField;
  order: SortOrder;
}

/** 並び替えキー（単位 = DisplayUnit）。比較グループはベース名 / 代表値で比較 */
const unitSortKey = (unit: DisplayUnit, field: SortField): string | number => {
  if (field === "name") {
    return unit.comparisonBase ?? unit.blocks[0].variation.name;
  }
  if (field === "file") {
    return unit.blocks[0].variation.fileName;
  }
  // date: グループ内の最新 updatedAt
  return Math.max(
    ...unit.blocks.map((block) => {
      const t = block.variation.updatedAt
        ? Date.parse(block.variation.updatedAt)
        : 0;
      return Number.isNaN(t) ? 0 : t;
    }),
  );
};

const sortUnits = (
  units: DisplayUnit[],
  { field, order }: SortState,
): DisplayUnit[] => {
  const dir = order === "asc" ? 1 : -1;
  return [...units].sort((a, b) => {
    const ka = unitSortKey(a, field);
    const kb = unitSortKey(b, field);
    const cmp =
      typeof ka === "number" && typeof kb === "number"
        ? ka - kb
        : String(ka).localeCompare(String(kb), "ja");
    return cmp * dir;
  });
};

// ===== サムネイルセル =====

interface ThumbCellProps {
  row: MediaTableRow;
  size: number;
}

const ThumbCell: React.FC<ThumbCellProps> = ({ row, size }) => {
  const src = row.thumbnail || row.mediaSrc;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 4,
        overflow: "hidden",
        background: "var(--soremo-background)",
        border: "1px solid var(--soremo-border)",
        flexShrink: 0,
      }}
    >
      {src ? (
        <MediaPreview
          src={src}
          sizeMode={{
            mode: "fixed-height" as const,
            height: `${size}px`,
            maxWidth: `${size}px`,
          }}
          autoPlay={false}
          controls={false}
          hoverPlay={false}
          disableHoverEffect
        />
      ) : (
        <div className="u-col-center u-w100" style={{ height: "100%" }}>
          <span
            className="material-symbols-rounded u-text-light-gray"
            style={{ fontSize: Math.round(size * 0.55) }}
          >
            {getFileIconFromExtension(row.fileName)}
          </span>
        </div>
      )}
    </div>
  );
};

// ===== インライン編集テキスト（名前セル / ファイル名セル共通） =====

interface InlineEditableTextProps {
  value: string;
  editable: boolean;
  /** 非編集時の表示クラス（typography） */
  displayClassName: string;
  /** 空値のときの表示 */
  emptyLabel?: string;
  /**
   * true のとき flex: 1 で伸ばさず内容幅に留める。
   * 文字起こしプレビューを名前の直後から続けたい行で使う。
   */
  fitContent?: boolean;
  /** 確定時（trim 済み・変更ありのときのみ呼ばれる） */
  // eslint-disable-next-line no-unused-vars
  onCommit?: (newValue: string) => void;
}

const InlineEditableText: React.FC<InlineEditableTextProps> = ({
  value,
  editable,
  displayClassName,
  emptyLabel = "（無題）",
  fitContent = false,
  onCommit,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!isEditing) setDraft(value);
  }, [value, isEditing]);

  const commit = () => {
    setIsEditing(false);
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value) return;
    onCommit?.(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsEditing(false);
      setDraft(value);
    }
  };

  if (isEditing && editable) {
    return (
      <input
        type="text"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        style={INLINE_INPUT_STYLE}
      />
    );
  }

  return (
    <ToolTip description={value || emptyLabel} truncatedOnly>
      <div
        className={displayClassName}
        onClick={editable ? () => setIsEditing(true) : undefined}
        style={{
          flex: fitContent ? "0 1 auto" : 1,
          minWidth: 0,
          minHeight: 22,
          lineHeight: "22px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: editable ? "text" : "default",
        }}
      >
        {value || emptyLabel}
      </div>
    </ToolTip>
  );
};

// ===== 名前セル（候補・履歴ともインライン編集可） =====

interface NameCellProps {
  row: MediaTableRow;
  editable: boolean;
  transcriptionStatus?: TranscriptionCellInfo;
  // eslint-disable-next-line no-unused-vars
  onRename?: (id: string, newName: string) => void;
}

const NameCell: React.FC<NameCellProps> = ({
  row,
  editable,
  transcriptionStatus,
  onRename,
}) => {
  const isHistory = row.kind === "history";

  // 完了時の本文プレビューは「名前の直後から残り幅いっぱい」に出す
  // （共有ライブラリと同じ見え方）。そのため名前側は内容幅に留め、
  // プレビュー側だけを伸縮させる。チップだけのときは従来どおり
  // 名前側を flex: 1 で伸ばし、チップは縮小させない。
  const showPreview = hasTranscriptionPreview(transcriptionStatus);

  const nameContent = (
    <InlineEditableText
      value={row.name}
      editable={editable}
      displayClassName={
        isHistory ? "bodytext-13 u-text-light-gray" : "bodytext-13"
      }
      fitContent={showPreview}
      onCommit={(newName) => onRename?.(row.id, newName)}
    />
  );

  // 名前の後ろに続くステータス表示（ステータスラベル + 文字起こしチップ / 本文プレビュー）
  const statusTrailing =
    row.statusLabel || transcriptionStatus ? (
      <span
        className="u-row u-gap4 u-items-center"
        style={
          showPreview
            ? { flex: "1 1 0", minWidth: 0, overflow: "hidden" }
            : { flexShrink: 0 }
        }
      >
        {row.statusLabel && (
          <MediaLabel tone={row.statusLabelTone ?? "blue"} size="small">
            {row.statusLabel}
          </MediaLabel>
        )}
        <TranscriptionStatusCell entry={transcriptionStatus} />
      </span>
    ) : null;

  if (isHistory) {
    return (
      <div
        className="u-row u-gap4 u-items-center"
        style={{ paddingLeft: 12, overflow: "hidden" }}
      >
        <span
          className="material-symbols-rounded u-text-light-gray"
          style={{ fontSize: 16, flexShrink: 0 }}
        >
          subdirectory_arrow_right
        </span>
        {row.takeNumber !== undefined && (
          <TakeLabel take={row.takeNumber} size="inline" />
        )}
        {nameContent}
        {statusTrailing}
      </div>
    );
  }

  if (statusTrailing) {
    return (
      <div
        className="u-row u-gap4 u-items-center"
        style={{ overflow: "hidden" }}
      >
        {nameContent}
        {statusTrailing}
      </div>
    );
  }

  return nameContent;
};

// ===== 行 =====

interface MediaRowProps {
  row: MediaTableRow;
  /** 並び替え単位（DisplayUnit）の先頭バリエーション行か（ドラッグハンドルを持つ） */
  isUnitHead: boolean;
  /** 所属する比較グループのベース名（単独バリエーションなら null） */
  comparisonBase: string | null;
  canDrag: boolean;
  showSize: boolean;
  showTags: boolean;
  showDate: boolean;
  showMenu: boolean;
  /** オーディオ行の試聴 props（行選択 + キーボード操作） */
  auditionRowProps?: AuditionRowProps;
  auditionSelected?: boolean;
  auditionPlaying?: boolean;
  onAuditionToggle?: () => void;
  // eslint-disable-next-line no-unused-vars
  onRename?: (id: string, newName: string) => void;
  // eslint-disable-next-line no-unused-vars
  onRenameFile?: (id: string, newFileName: string) => void;
  // eslint-disable-next-line no-unused-vars
  onDownload?: (id: string) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete?: (id: string) => void;
  // eslint-disable-next-line no-unused-vars
  onTranscribe?: (id: string) => void;
  transcriptionStatus?: TranscriptionCellInfo;
}

const MediaRow: React.FC<MediaRowProps> = ({
  row,
  isUnitHead,
  comparisonBase,
  canDrag,
  showSize,
  showTags,
  showDate,
  showMenu,
  auditionRowProps,
  auditionSelected,
  auditionPlaying,
  onAuditionToggle,
  onRename,
  onRenameFile,
  onDownload,
  onDelete,
  onTranscribe,
  transcriptionStatus,
}) => {
  const isVariation = row.kind === "variation";
  const draggable = isVariation && isUnitHead && canDrag;

  // D&D 対象外の行も hooks の数を揃えるため常に useSortable を呼び、
  // disabled で無効化する。
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: row.id,
    disabled: !draggable,
  });

  const {
    ref: auditionRef,
    style: auditionStyle,
    ...auditionRest
  } = auditionRowProps ?? {};

  const rowStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging
      ? "var(--soremo-background)"
      : auditionSelected
        ? "rgba(0, 154, 206, 0.06)"
        : undefined,
    ...auditionStyle,
  };

  // 3点リーダーのメニュー（トークルームのファイル一覧と同じ構成。
  // 共有・差し替えは対応時にここへ項目を足す）
  const menuItems = useMemo<DropdownMenuItem[]>(() => {
    const items: DropdownMenuItem[] = [];
    if (onDownload) {
      items.push({
        label: "ダウンロード",
        icon: "download",
        onClick: () => onDownload(row.id),
      });
    }
    if (onTranscribe && isTranscribableFileType(row.fileType)) {
      items.push({
        label: "文字起こし",
        icon: "speech_to_text",
        onClick: () => onTranscribe(row.id),
      });
    }
    if (onDelete) {
      items.push({
        label: "削除",
        icon: "delete",
        onClick: () => onDelete(row.id),
      });
    }
    return items;
  }, [onDownload, onDelete, onTranscribe, row.id, row.fileType]);

  const inComparison = comparisonBase !== null;

  return (
    <tr
      ref={(el) => {
        setNodeRef(el);
        auditionRef?.(el);
      }}
      {...auditionRest}
      style={rowStyle}
      className={isVariation ? undefined : "table-row-dimmed"}
    >
      {/* ドラッグハンドル（並び替え単位の先頭のみ）/ 比較グループの連結アイコン */}
      <td
        style={{
          textAlign: "center",
          ...(inComparison ? COMPARISON_ACCENT_STYLE : null),
        }}
      >
        {isVariation && isUnitHead && (
          <span
            {...attributes}
            {...listeners}
            aria-label="並び替え"
            className="material-symbols-rounded u-text-light-gray"
            style={{
              fontSize: 18,
              cursor: canDrag ? "grab" : "default",
              touchAction: "none",
              display: "inline-flex",
            }}
          >
            drag_indicator
          </span>
        )}
        {isVariation && !isUnitHead && inComparison && (
          <ToolTip
            description={`「${comparisonBase}」のオプション（候補）としてまとめて比較表示・移動されます`}
          >
            <span
              className="material-symbols-rounded u-text-light-gray"
              style={{ fontSize: 16, display: "inline-flex" }}
            >
              link
            </span>
          </ToolTip>
        )}
      </td>

      {/* サムネ（オーディオ行は試聴再生ボタン） */}
      <td>
        {onAuditionToggle ? (
          <AuditionPlayButton
            playing={!!auditionPlaying}
            size={isVariation ? 32 : 24}
            onToggle={onAuditionToggle}
          />
        ) : (
          <ThumbCell row={row} size={isVariation ? 32 : 24} />
        )}
      </td>

      {/* 名前（履歴行はインデント + テイクバッジ付き。どちらも編集可） */}
      <td>
        <NameCell
          row={row}
          editable={!!onRename}
          transcriptionStatus={transcriptionStatus}
          onRename={onRename}
        />
      </td>

      {/* ファイル名（onRenameFile 指定時はインライン編集可） */}
      <td>
        <InlineEditableText
          value={row.fileName}
          editable={!!onRenameFile}
          displayClassName="bodytext-13 u-text-light-gray"
          onCommit={(newFileName) => onRenameFile?.(row.id, newFileName)}
        />
      </td>

      {/* サイズ */}
      {showSize && (
        <td>
          <span className="bodytext-13 u-text-light-gray">
            {row.sizeLabel ?? "—"}
          </span>
        </td>
      )}

      {/* タグ（送信側 / 受信側） */}
      {showTags && (
        <td>
          {row.producerTag || row.clientTag ? (
            <div
              className="u-row u-gap4"
              style={{ flexWrap: "nowrap", overflow: "hidden" }}
            >
              {row.producerTag && (
                <MediaLabel
                  tone={row.producerTagTone ?? "outline"}
                  size="medium"
                >
                  {row.producerTag}
                </MediaLabel>
              )}
              {row.clientTag && <ResponseChip>{row.clientTag}</ResponseChip>}
            </div>
          ) : (
            <span className="bodytext-13 u-text-light-gray">—</span>
          )}
        </td>
      )}

      {/* 更新日時 */}
      {showDate && (
        <td>
          {row.updatedAt ? (
            <span className="bodytext-13 u-text-light-gray">
              <DateDisplay
                date={row.updatedAt}
                type={DATE_DISPLAY_TYPE.DATE_TIME}
              />
            </span>
          ) : (
            <span className="bodytext-13 u-text-light-gray">—</span>
          )}
        </td>
      )}

      {/* 操作メニュー（3点リーダー: ダウンロード / 削除） */}
      {showMenu && (
        <td style={{ textAlign: "center" }} className="table-cell-visible">
          {menuItems.length > 0 && <DropdownMenu items={menuItems} />}
        </td>
      )}
    </tr>
  );
};

// ===== 本体 =====

const MediaItemTable: React.FC<MediaItemTableProps> = ({
  rows,
  onRename,
  onRenameFile,
  onReorder,
  onDownload,
  onDelete,
  onTranscribe,
  transcriptionStatuses,
  emptyMessage = "メディアアイテムがありません",
  helpText,
  minWidth = 560,
  onFilesSelected,
  accept,
  addFilesDisabled = false,
  uploadingRow,
}) => {
  const units = useMemo(() => buildDisplayUnits(rows), [rows]);

  // ===== ファイル追加（テーブル全体へのドロップ + 末尾「+」行のファイル選択） =====
  // 行の並び替え D&D は dnd-kit（Pointer イベント）、ファイル投下は HTML5
  // ネイティブ drag なのでイベント系統が異なり衝突しない。isDragActive は
  // react-dropzone がファイルを含むドラッグのみで立てる。
  const filesEnabled = !!onFilesSelected;
  // MIME が空になる端末（Windows の一部）でも弾かれないよう拡張子を補う
  const acceptWithExtensions = useMemo(
    () => (accept ? withExtensionFallback(accept) : undefined),
    [accept],
  );
  const {
    getRootProps: getFileDropRootProps,
    getInputProps: getFileDropInputProps,
    isDragActive: isFileDragActive,
    open: openFileDialog,
  } = useDropzone({
    onDrop: (accepted: File[]) => {
      if (accepted.length > 0) onFilesSelected?.(accepted);
    },
    // 弾かれたファイルが黙って消えると、ドロップした本人が漏れに気付けない
    onDropRejected: (rejections: FileRejection[]) =>
      rejections.forEach((rejection) =>
        toast.error(formatDropRejection(rejection)),
      ),
    accept: acceptWithExtensions,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: !filesEnabled || addFilesDisabled,
  });

  // オーディオ行を表示順に集めて試聴対象にする（行選択 + ↑↓ + Space）
  const auditionItems = useMemo<AuditionItem[]>(() => {
    const items: AuditionItem[] = [];
    units.forEach((unit) =>
      unit.blocks.forEach((block) => {
        [block.variation, ...block.history].forEach((row) => {
          if (isAudioRow(row))
            items.push({ key: row.id, src: row.mediaSrc as string });
        });
      }),
    );
    return items;
  }, [units]);
  const audition = useAudioAudition(auditionItems);

  const showDate = rows.some((r) => r.updatedAt);
  const showSize = rows.some((r) => r.sizeLabel !== undefined);
  const showTags = rows.some(
    (r) => r.producerTag !== undefined || r.clientTag !== undefined,
  );
  const showMenu = !!(onDownload || onDelete || onTranscribe);
  // D&D とヘッダーソートはどちらも onReorder 経由の並び替えなので条件を共有する
  const canReorder = !!onReorder && units.length > 1;

  const [sortState, setSortState] = useState<SortState | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onReorder) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const unitIds = units.map((u) => u.id);
    const oldIndex = unitIds.indexOf(String(active.id));
    const newIndex = unitIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    // 手動並び替えしたらソート表示は解除する
    setSortState(null);
    onReorder(flattenVariationIds(arrayMove(units, oldIndex, newIndex)));
  };

  const handleSort = (field: SortField) => {
    if (!onReorder) return;
    const order: SortOrder =
      sortState?.field === field && sortState.order === "asc" ? "desc" : "asc";
    const next: SortState = { field, order };
    setSortState(next);
    onReorder(flattenVariationIds(sortUnits(units, next)));
  };

  const sortHeader = (label: string, field: SortField) => (
    <>
      <span>{label}</span>
      {sortState?.field === field && (
        <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
          {sortState.order === "asc" ? "arrow_upward" : "arrow_downward"}
        </span>
      )}
    </>
  );

  const sortableHeaderProps = (field: SortField) =>
    canReorder
      ? {
          onHeaderClick: () => handleSort(field),
          headerTitle: "クリックでソート（グループは崩れません）",
        }
      : {};

  const columns: TableColumn[] = [
    { key: "drag", width: 36, minWidth: 36, resizable: false },
    { key: "thumb", header: "", width: 48, minWidth: 40, resizable: false },
    {
      key: "name",
      header: sortHeader("名前", "name"),
      minWidth: 160,
      ...sortableHeaderProps("name"),
    },
    {
      key: "file",
      header: sortHeader("ファイル名", "file"),
      width: 160,
      minWidth: 100,
      ...sortableHeaderProps("file"),
    },
    ...(showSize
      ? [
          {
            key: "size",
            header: "サイズ",
            width: 88,
            minWidth: 64,
          } as TableColumn,
        ]
      : []),
    ...(showTags
      ? [
          {
            key: "tags",
            header: "タグ",
            width: 140,
            minWidth: 88,
          } as TableColumn,
        ]
      : []),
    ...(showDate
      ? [
          {
            key: "date",
            header: sortHeader("更新日時", "date"),
            width: 130,
            minWidth: 100,
            ...sortableHeaderProps("date"),
          } as TableColumn,
        ]
      : []),
    ...(showMenu
      ? [
          {
            key: "menu",
            header: "",
            width: 40,
            minWidth: 40,
            resizable: false,
          } as TableColumn,
        ]
      : []),
  ];
  const columnCount = columns.length;

  const renderRow = (
    row: MediaTableRow,
    isUnitHead: boolean,
    comparisonBase: string | null,
  ) => {
    const auditionable = isAudioRow(row);
    return (
      <MediaRow
        key={row.id}
        row={row}
        isUnitHead={isUnitHead}
        comparisonBase={comparisonBase}
        canDrag={canReorder}
        showSize={showSize}
        showTags={showTags}
        showDate={showDate}
        showMenu={showMenu}
        auditionRowProps={
          auditionable ? audition.getRowProps(row.id) : undefined
        }
        auditionSelected={auditionable && audition.selectedKey === row.id}
        auditionPlaying={auditionable && audition.playingKey === row.id}
        onAuditionToggle={
          auditionable ? () => audition.toggle(row.id) : undefined
        }
        onRename={onRename}
        onRenameFile={onRenameFile}
        onDownload={onDownload}
        onDelete={onDelete}
        onTranscribe={onTranscribe}
        transcriptionStatus={transcriptionStatuses?.[row.id]}
      />
    );
  };

  const renderRows = () =>
    units.flatMap((unit) =>
      unit.blocks.flatMap((block, blockIdx) => [
        renderRow(block.variation, blockIdx === 0, unit.comparisonBase),
        ...block.history.map((take) =>
          renderRow(take, false, unit.comparisonBase),
        ),
      ]),
    );

  return (
    <>
      {helpText && (
        <div className="u-row u-w100 u-justify-end">
          <HelpHint description={helpText} ariaLabel="メディア一覧の使い方" />
        </div>
      )}
      {/* ファイルのドロップを受ける器。横スクロール・枠・余白は Table 内蔵の
          スクロール領域に任せ、ここはドロップ中のハイライトだけを担う。 */}
      <div
        className="u-w100"
        {...(filesEnabled ? getFileDropRootProps() : {})}
        style={{
          borderRadius: 4,
          ...(isFileDragActive
            ? {
                outline: "2px dashed var(--soremo-blue)",
                outlineOffset: "-2px",
                background: "rgba(0, 154, 206, 0.05)",
              }
            : {}),
        }}
      >
        {filesEnabled && <input {...getFileDropInputProps()} />}
        <Table columns={columns} minWidth={minWidth}>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columnCount}>
                <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                  {emptyMessage}
                </p>
              </td>
            </tr>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={units.map((u) => u.id)}
                strategy={verticalListSortingStrategy}
              >
                {renderRows()}
              </SortableContext>
            </DndContext>
          )}

          {/* アップロード進捗などの全幅差し込み行 */}
          {uploadingRow && (
            <tr>
              <td colSpan={columnCount}>{uploadingRow}</td>
            </tr>
          )}

          {/* 末尾「+」行: 新規ファイルの選択 / D&D 追加の入口 */}
          {filesEnabled && rows.length > 0 && (
            <tr>
              <td colSpan={columnCount}>
                <TableAddFileRow
                  onClick={openFileDialog}
                  disabled={addFilesDisabled}
                  label="クリックしてファイルを追加（表へのドラッグ＆ドロップも可）"
                  ariaLabel="ファイルを追加"
                />
              </td>
            </tr>
          )}
        </Table>
      </div>
    </>
  );
};

export default MediaItemTable;
